import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Documentation } from 'graphql-common';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Modal from '@lib/components/Modal/Modal';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { ModalMode } from '@lib/components/Modal/enums';
import Table from '@lib/components/Table/Table';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import { APP_URLS } from 'constants/urls';
import useAiIndexingDocumentationsQueryHook from 'utils/fetch/hooks/useAiIndexingDocumentationsQueryHook';
import { PARKED_FILTERS } from '../utils/getIndexingDocumentFilters';
import getIndexingDocumentsTableColumns from '../utils/getIndexingDocumentsTableColumns';

// The shared ORDERING_PARAMS default to `createdAt`, which no column here shows
// — the sort indicator would then point at nothing. Neither indexing axis is
// sortable server-side, so name is the only real choice.
const DEFAULT_ORDERING: OrderingParams = {
  [GetUrlParams.OrderingField]: 'name',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
};

function ParkedDocuments() {
  const { t } = useTranslation();
  // Kept in local state rather than the URL: the modal sits on the Company plan
  // settings page and must not rewrite its query string.
  const paginationState = useState<PaginationParams>(PAGINATION_PARAMS);
  const orderingState = useState<OrderingParams>(DEFAULT_ORDERING);
  const [paginationParams] = paginationState;
  const [orderingParams] = orderingState;

  const { collection, loading, totalCount } =
    useAiIndexingDocumentationsQueryHook({
      filters: PARKED_FILTERS,
      orderingParams,
      paginationParams,
    });

  // A new tab, not a navigation: this modal is where an admin works through the
  // parked set one document at a time, and it also carries the Retry action.
  // Navigating in place would tear both down after the first document.
  const onOpen = useCallback((item: Documentation) => {
    const to = APP_URLS.dashboard.documentation.view.getDynamicPath({
      pathParts: { id: String(item.id) },
    });
    window.open(to, '_blank')?.focus();
  }, []);

  const columns = useMemo(
    () => getIndexingDocumentsTableColumns({ onOpen, t }),
    [onOpen, t],
  );

  return (
    <Table
      columns={columns}
      // `documentations` rows are already BaseItems, so no reshaping step.
      data={collection}
      loading={loading}
      orderingState={orderingState}
      paginationState={paginationState}
      // Equals the card's `parked` by construction — the card counts the whole
      // company, and a full admin's document scope IS the whole company — so
      // the two numbers on screen can never disagree.
      totalEntries={totalCount}
    />
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onRetryParked: () => void;
  retryParkedLoading?: boolean;
};

/**
 * Which documents the indexing card's parked count is counting.
 *
 * **Full admin on the AI plan only** — the query behind it filters on
 * `aiIndexingRetryLane`, and a rejected filter nulls the entire response rather
 * than dropping a field, so the caller opens this only for that audience.
 *
 * Retry rides along at the bottom: it is the same action the banner offers, and
 * the list is exactly where an admin decides whether retrying is worth it at
 * all — a file parked as unreadable fails again unless it is replaced first.
 */
function ParkedDocumentsModal({
  isOpen,
  onClose,
  onRetryParked,
  retryParkedLoading,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      mode={ModalMode.FullScreen}
      onClose={onClose}
      title={t('ai-parked-documents')}
      description={t('ai-parked-documents-description')}
      fixedActions={
        <ActionsRow
          actions={[
            <Button
              key="close"
              buttonSize={ButtonSizes.small}
              buttonText={t('close')}
              buttonType={ButtonTypes.secondaryOutlined}
              data-test-id="parked-documents__close-button"
              onClick={onClose}
              fullWidth
            />,
            <Button
              key="retry"
              buttonSize={ButtonSizes.small}
              buttonText={t('ai-retry-parked')}
              buttonType={ButtonTypes.primaryFilled}
              data-test-id="parked-documents__retry-button"
              disabled={retryParkedLoading}
              isLoading={retryParkedLoading}
              leftIcon="restart_alt"
              // Called with no arguments on purpose: the caller passes an Apollo
              // mutate function straight in, and `Button` invokes
              // `onClick(event)` — which would hand the click event to the
              // mutation as its options object.
              onClick={() => onRetryParked()}
              fullWidth
            />,
          ]}
        />
      }
    >
      {isOpen ? <ParkedDocuments /> : <div />}
    </Modal>
  );
}

export default memo(ParkedDocumentsModal);
